/**
 * Application routes configuration
 * Centralized place to define all route paths for reusability
 */

import { useMatch } from 'react-router-dom';

// Parent route path constants
export const PARENT_ROUTES = {
  HOME: '/',
  ORG: '/organizations/:orgId',
  WORKSPACE: '/organizations/:orgId/workspaces/:workspaceId',
} as const;

// Onboarding step constants
export const ONBOARDING_STEPS = {
  USER_INFORMATION: 'user-information',
  ORGANIZATION: 'organization',
  WORKSPACE: 'workspace',
  USER_INTENT: 'user-intent',
} as const;

export type TOnboardingStep =
  (typeof ONBOARDING_STEPS)[keyof typeof ONBOARDING_STEPS];

// Route parameter types
export interface IRouteParams {
  [key: string]: string | number;
}

// Route pattern type - for defining route patterns with placeholders
export interface IRoutePattern {
  pattern: string;
  build: (params: IRouteParams) => string;
}

// Route definition type - can be a string, function, or pattern object
export type TRouteDefinition =
  | string
  | ((params: IRouteParams) => string)
  | IRoutePattern;

// Routes configuration
export const ROUTES = {
  // Main routes (no params)
  HOME: '/',
  NOT_FOUND: '/not-found',
  LOGIN: '/login',
  LOGOUT: '/logout',
  /** Redirect URI of a federated (OIDC) provider. */
  CALLBACK: {
    pattern: '/callback/:providerKey',
    build: (params: IRouteParams) =>
      `/callback/${encodeURIComponent(String(params.providerKey))}`,
  },
  SIGNUP: '/signup',
  PREVIEW_MAP_AND_RUN_LOG: '/preview/map-and-run-log',
  ONBOARDING: {
    pattern: '/onboarding/:onboardingStep?',
    build: (params: IRouteParams) => {
      const searchParams = new URLSearchParams();
      if (params.hasExistingOrg) {
        searchParams.set('hasExistingOrg', String(params.hasExistingOrg));
      }
      if (params.orgId) {
        searchParams.set('orgId', String(params.orgId));
      }
      const path = `/onboarding/${params.onboardingStep ?? ''}`;
      const query = searchParams.toString();
      return query ? `${path}?${query}` : path;
    },
  },
  INVITATION: {
    pattern: '/onboarding/invite/:invitationId',
    build: (params: IRouteParams) =>
      `/onboarding/invite/${params.invitationId}`,
  },
  PREFERENCES: '/preferences',
  TUTORIALS: '/tutorials',
  USER_SETTINGS: '/settings/user',
  USER_SETTINGS_PROFILE: '/settings/user/profile',
  // Organization routes (only orgId)
  ORGANIZATION_PERFORMANCE: {
    pattern: '/organizations/:orgId/performance',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/performance`,
  },
  ORGANIZATION_CONSUMPTION: {
    pattern: '/organizations/:orgId/consumption',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/consumption`,
  },
  // Org-level so an owner can manage any workspace without switching the one
  // they are working in; the scope travels as `?scope=<workspaceId>`.
  ROLES_AND_ACCESS: {
    pattern: '/organizations/:orgId/roles-and-access',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/roles-and-access`,
  },
  REMOTE_CONNECTIONS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/remote-connections',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/remote-connections`,
  },
  REMOTE_CONNECTIONS_CREATE: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/remote-connections/create',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/remote-connections/create`,
  },
  REMOTE_CONNECTIONS_DETAIL: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/remote-connections/:remoteConnectionId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/remote-connections/${params.remoteConnectionId}`,
  },
  REMOTE_CONNECTIONS_EDIT: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/remote-connections/:remoteConnectionId/edit',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/remote-connections/${params.remoteConnectionId}/edit`,
  },
  API_KEYS: {
    pattern: '/organizations/:orgId/api-keys',
    build: (params: IRouteParams) => `/organizations/${params.orgId}/api-keys`,
  },
  ASSUME_SUPPORT: {
    pattern: '/assume-support/:supportId',
    build: (params: IRouteParams) => {
      const searchParams = new URLSearchParams();
      if (params.orgId) {
        searchParams.set('orgId', String(params.orgId));
      }
      if (params.workspaceId) {
        searchParams.set('workspaceId', String(params.workspaceId));
      }
      const path = `/assume-support/${params.supportId}`;
      const query = searchParams.toString();
      return query ? `${path}?${query}` : path;
    },
  },
  AUDIT_LOG: {
    pattern: '/organizations/:orgId/audit-log',
    build: (params: IRouteParams) => `/organizations/${params.orgId}/audit-log`,
  },

  // Workspace routes (orgId + workspaceId)
  WORKSPACE: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}`,
  },
  DRAFTS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/drafts',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/drafts`,
  },
  DRAFT_NEW: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/drafts/new',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/drafts/new`,
  },
  DRAFT_EDITOR: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/drafts/:draftId/edit',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/drafts/${params.draftId}/edit`,
  },
  DRAFT_ACTIVITY: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/drafts/:draftId/activity',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/drafts/${params.draftId}/activity`,
  },
  DRAFT_PRE_RUN: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/drafts/:draftId/run',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/drafts/${params.draftId}/run`,
  },
  DRAFT_RUN: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/drafts/:draftId/runs/:runId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/drafts/${params.draftId}/runs/${params.runId}`,
  },
  AUTOMATIONS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/automations',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations`,
  },
  AGENTS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/agents',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/agents`,
  },
  /** The Apps catalog: pre-made apps over agent and automation blueprints. */
  APPS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/apps',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/apps`,
  },
  /** One app, running, with its dependencies and its chat. */
  APP_DETAIL: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/apps/:appId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/apps/${params.appId}`,
  },
  /**
   * "Create a new agent" page: the prompt composer plus the template gallery.
   * Its pattern must be registered BEFORE `AGENT_BUILD`, whose `:agentId` param
   * would otherwise match `/agents/new` and open a builder for an agent with the
   * literal id "new".
   */
  AGENT_NEW: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/agents/new',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/agents/new`,
  },
  /**
   * The build canvas with sample data: the plan Smith would lay out, built and
   * tested locally, so the experience can be tried without a builder agent.
   * Static, so it's registered before `AGENT_BUILD`'s `:agentId` match.
   */
  AGENT_CANVAS_PREVIEW: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/agents/new/canvas',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/agents/new/canvas`,
  },
  /** One agent's build canvas, drawn from the plan its builder keeps. */
  AGENT_CANVAS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/agents/:agentId/canvas',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/agents/${params.agentId}/canvas`,
  },
  /**
   * The agent detail/overview page: stats, waiting-on-you, and its threads.
   * "Open in builder" from here leads to `AGENT_BUILD_EDIT`.
   */
  AGENT_BUILD: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/agents/:agentId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/agents/${params.agentId}`,
  },
  /**
   * The conversational builder for the same agent. The extra trailing `/build`
   * segment means this never collides with `AGENT_BUILD`'s own `:agentId`
   * match regardless of registration order.
   */
  AGENT_BUILD_EDIT: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/agents/:agentId/build',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/agents/${params.agentId}/build`,
  },
  CARTOGRAPHER: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/context-graphs',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/context-graphs`,
  },
  /** A context graph's Definition: the Cartographer chat beside its document. */
  CONTEXT_GRAPH: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/context-graphs/:graphId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/context-graphs/${encodeURIComponent(String(params.graphId))}`,
  },
  /** A context graph's Activity: its refreshes and monitor runs. */
  CONTEXT_GRAPH_ACTIVITY: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/context-graphs/:graphId/activity',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/context-graphs/${encodeURIComponent(String(params.graphId))}/activity`,
  },
  LEARNINGS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/learnings',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/learnings`,
  },
  GUIDANCE_CENTER: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/guidance-center',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/guidance-center`,
  },
  WORKBENCH: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/workbench',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/workbench`,
  },

  LEGACY_TROUBLESHOOTING_GUIDE: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/automations/:automationId/troubleshooting-guide/:entryId?',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations/${params.automationId}/troubleshooting-guide${params.entryId ? `/${params.entryId}` : ''}`,
  },
  RESOLUTION_PLAYBOOK: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/automations/:automationId/resolution-playbook',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations/${params.automationId}/resolution-playbook`,
  },
  RESOLUTION_PLAYBOOK_ENTRY_DETAILS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/automations/:automationId/resolution-playbook/:entryId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations/${params.automationId}/resolution-playbook/${params.entryId}`,
  },
  ACTION_ITEM_DETAIL: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/guidance-center/:actionItemId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/guidance-center/${params.actionItemId}`,
  },
  ACTION_ITEM_REVIEW: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/guidance-center/:actionItemId/review',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/guidance-center/${params.actionItemId}/review`,
  },
  AUTOMATION_AOP: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/automations/:automationId/aop',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations/${params.automationId}/aop`,
  },
  AUTOMATION_DETAILS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/automations/:automationId/details',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations/${params.automationId}/details`,
  },
  AUTOMATION_PRE_RUN: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/automations/:automationId/run',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations/${params.automationId}/run`,
  },
  AUTOMATION_RUN: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/automations/:automationId/runs/:runId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations/${params.automationId}/runs/${params.runId}`,
  },
  AUTOMATION_STEP_WORKBENCH: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/automations/:automationId/steps/:stepId/workbench',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations/${params.automationId}/steps/${params.stepId}/workbench`,
  },
  // Whole-automation testing — the same workbench with no step segment. Listed
  // after the step route so `/steps/:stepId/workbench` still wins its match.
  AUTOMATION_WORKBENCH: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/automations/:automationId/workbench',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations/${params.automationId}/workbench`,
  },

  // Integrations routes (orgId + workspaceId)
  INTEGRATIONS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/integrations',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/integrations`,
  },
  INTEGRATIONS_CONNECTIONS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/integrations/connections',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/integrations/connections`,
  },
  INTEGRATIONS_INTEGRATION_DETAILS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/integrations/:integrationName/:integrationVersion',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/integrations/${params.integrationName}/${params.integrationVersion}`,
  },

  // Library routes (orgId + workspaceId)
  LIBRARY: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/library',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/library`,
  },
  LIBRARY_COLLECTIONS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/library/collections',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/library/collections`,
  },
  LIBRARY_COLLECTION: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/library/collections/:collectionId',
    build: (params: IRouteParams) => {
      const path = `/organizations/${params.orgId}/workspaces/${params.workspaceId}/library/collections/${params.collectionId}`;
      return params.stage === 'published' ? `${path}?stage=published` : path;
    },
  },
  LIBRARY_FOLDER: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/library/folders/:folderId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/library/folders/${params.folderId}`,
  },

  // Finance workspace routes (orgId + workspaceId) — surfaced only in a
  // finance-typed workspace (WORKSPACE_TYPE_FINANCE), enforced by FinanceLayout
  // / useIsFinanceWorkspace.
  FINANCE: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance`,
  },
  FINANCE_FINDINGS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/findings',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/findings`,
  },
  FINANCE_DETECTORS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/detectors',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/detectors`,
  },
  FINANCE_LEARNINGS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/learnings',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/learnings`,
  },
  FINANCE_INVESTIGATIONS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/investigations',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/investigations`,
  },
  /**
   * Case-file resolver. Mirrors argus's resource tree (an Investigation is
   * nested under its Finding) but stops at the collection: list rows link here
   * with only the finding id they already have, and this route resolves — or
   * opens — the session before redirecting to the canonical URL below. Without
   * that split every sidebar row would need a per-finding ListInvestigations.
   */
  FINANCE_FINDING_INVESTIGATIONS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/findings/:findingId/investigations',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/findings/${params.findingId}/investigations`,
  },
  /** One agent-chat session against a finding. A pure read — creates nothing. */
  FINANCE_FINDING_INVESTIGATION: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/findings/:findingId/investigations/:investigationId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/findings/${params.findingId}/investigations/${params.investigationId}`,
  },
  FINANCE_EXCEPTIONS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/exceptions',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/exceptions`,
  },
  FINANCE_EXCEPTION_DETAIL: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/exceptions/:exceptionId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/exceptions/${params.exceptionId}`,
  },
  FINANCE_ARCHITECTURE: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/architecture',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/architecture`,
  },
  FINANCE_TOUCHLESS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/touchless',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/touchless`,
  },
  FINANCE_AUDIT: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/audit',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/audit`,
  },
  FINANCE_INTAKE: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/intake',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/intake`,
  },
  FINANCE_INTAKE_DETAIL: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/intake/:mailboxId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/intake/${params.mailboxId}`,
  },
  FINANCE_ADMIN_CALIBRATION: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/admin/calibration',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/admin/calibration`,
  },
  FINANCE_ADMIN_COMPLIANCE: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/admin/compliance',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/admin/compliance`,
  },
  FINANCE_ADMIN_SETTINGS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/admin/settings',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/admin/settings`,
  },
  FINANCE_ADMIN_CONTROLS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/admin/controls',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/admin/controls`,
  },
  FINANCE_CONNECT: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/connect',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/connect`,
  },
  FINANCE_ONBOARDING: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/onboarding',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/onboarding`,
  },
  FINANCE_SOP_VAULT: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/sop-vault',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/sop-vault`,
  },
  FINANCE_CONTEXT_GRAPH: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/context-graph',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/context-graph`,
  },
  FINANCE_AGENT_NEW: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/agents/new',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/agents/new`,
  },
  FINANCE_AGENT_DETAIL: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/agents/:agentKey',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/agents/${params.agentKey}`,
  },
  FINANCE_APPROVALS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/approvals',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/approvals`,
  },
  FINANCE_JOURNAL_ENTRIES: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/journal-entries',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/journal-entries`,
  },
  FINANCE_JOURNAL_ENTRY_NEW: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/journal-entries/new',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/journal-entries/new`,
  },
  FINANCE_JOURNAL_ENTRY_DETAIL: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/journal-entries/:jeId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/journal-entries/${params.jeId}`,
  },
  FINANCE_RESOLVER: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/resolver',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/resolver`,
  },
  FINANCE_WORKSTREAM: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/workstreams/:workstream',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/workstreams/${params.workstream}`,
  },
  FINANCE_COCKPIT: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/cockpit',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/cockpit`,
  },
  // Legacy spelling of FINANCE_COCKPIT, kept resolvable because the URL was
  // shared widely while the live cockpit was still a POC. The route redirects.
  FINANCE_COCKPIT_POC: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/cockpit-poc',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/cockpit-poc`,
  },
  // The URL the redesign was reviewed at. Like FINANCE_COCKPIT_POC, it stays
  // resolvable and the route redirects to FINANCE_COCKPIT.
  FINANCE_COCKPIT_V2: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/cockpit-v2',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/cockpit-v2`,
  },
  FINANCE_EVIDENCE: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/evidence',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/evidence`,
  },
  FINANCE_FLEET: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/finance/fleet',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/fleet`,
  },
  FINANCE_FLEET_DISCOVERY: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/fleet/discovery',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/fleet/discovery`,
  },
  FINANCE_FLEET_WORKSTREAM: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/finance/fleet/:workstream',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/finance/fleet/${params.workstream}`,
  },

  // Workspace Settings (orgId + workspaceId)
  SETTINGS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/settings',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/settings`,
  },
  WORKSPACE_SETTINGS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/settings',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/settings`,
  },
  WORKSPACE_SETTINGS_OVERVIEW: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/settings/overview',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/settings/overview`,
  },
  // Legacy: now redirects to Roles & Access. Kept because the URL was public.
  WORKSPACE_SETTINGS_PEOPLE: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/settings/users',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/settings/users`,
  },
  WORKSPACE_SETTINGS_PREFERENCES: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/settings/preferences',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/settings/preferences`,
  },
  WORKSPACE_SETTINGS_NOTIFICATIONS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/settings/notifications',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/settings/notifications`,
  },
  WORKSPACE_SETTINGS_NOTIFICATION_CHANNEL: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/settings/notifications/:channel',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/settings/notifications/${params.channel}`,
  },
  WORKSPACE_SETTINGS_SUPPORT_ACCESS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/settings/support-access',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/settings/support-access`,
  },
  WORKSPACE_ACCESS_DENIED: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/access-denied',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/access-denied`,
  },

  // Org Settings (only orgId)
  ORG_SETTINGS: {
    pattern: '/organizations/:orgId/settings',
    build: (params: IRouteParams) => `/organizations/${params.orgId}/settings`,
  },
  ORG_SETTINGS_OVERVIEW: {
    pattern: '/organizations/:orgId/settings/overview',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/settings/overview`,
  },
  ORG_SETTINGS_WORKSPACES: {
    pattern: '/organizations/:orgId/settings/workspaces',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/settings/workspaces`,
  },
  // Legacy: now redirects to Roles & Access. Kept because the URL was public.
  ORG_SETTINGS_PEOPLE: {
    pattern: '/organizations/:orgId/settings/users',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/settings/users`,
  },
  ORG_SETTINGS_PREFERENCES: {
    pattern: '/organizations/:orgId/settings/preferences',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/settings/preferences`,
  },
  ORG_SETTINGS_SUPPORT_ACCESS: {
    pattern: '/organizations/:orgId/settings/support-access',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/settings/support-access`,
  },
  ORG_SETTINGS_REMOTE_SUPPORT: {
    pattern: '/organizations/:orgId/settings/remote-support',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/settings/remote-support`,
  },
} as const satisfies Record<string, TRouteDefinition>;

/**
 * Type for route keys
 */
export type TRouteKey = keyof typeof ROUTES;

/**
 * Helper function to get route path by key
 */
export const getRoutePath = (key: TRouteKey, params?: IRouteParams) => {
  const route = ROUTES[key];
  if (typeof route === 'function') {
    if (!params) {
      throw new Error(
        `Route ${key} requires parameters but none were provided`,
      );
    }
    return (route as (params: IRouteParams) => string)(params);
  }

  if (typeof route === 'object' && route !== null && 'pattern' in route) {
    if (!params) {
      return route.pattern;
    }
    return route.build(params);
  }

  return route;
};

/**
 * Helper function to check if a path matches a route
 */
export const isRoute = (
  path: string,
  route: TRouteKey,
  params?: IRouteParams,
): boolean => {
  const routePath = getRoutePath(route, params);
  return path === routePath;
};

/**
 * Helper function to get route pattern (useful for React Router)
 */
export const getRoutePattern = (key: TRouteKey): string => {
  const route = ROUTES[key];

  if (typeof route === 'string') {
    return route;
  }

  if (typeof route === 'object' && 'pattern' in route) {
    return route.pattern;
  }

  // For function-based routes, we can't determine the pattern
  // You might want to add a pattern property to function-based routes as well
  throw new Error(`Route ${key} does not have a defined pattern`);
};

export const useIsOnboardingOrInvitationPage = () => {
  const isOnboardingPage = Boolean(useMatch(getRoutePattern('ONBOARDING')));

  const isAcceptInvitationPage = Boolean(
    useMatch(getRoutePattern('INVITATION')),
  );

  return isOnboardingPage || isAcceptInvitationPage;
};

export const constructInvitationUrl = ({
  invitationId,
  organizationId,
}: {
  invitationId: string;
  organizationId: string;
}) => {
  return `${window.location.origin}${getRoutePath('INVITATION', { invitationId })}?organizationId=${organizationId}`;
};
